import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  AmountInput,
  Button,
  CurrenciesUSD,
  Dropdown,
  DropdownOption,
  Icon,
  Input,
  formatNumber,
} from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { RenderFields, RestrictedBanner } from '~/components';
import { LoanDropdown } from '~/components/fields/LoanDropdown';
import { useNavigate } from '~/lib/navigation';
import { LoanRepository } from '~/repositories';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { EditToolbar, Inner } from '~/styles';
import { CreateLoanPaymentRequest } from '~/typings/API';

type CreateLoanPaymentRequestForm = Omit<
  CreateLoanPaymentRequest,
  'principleBalance' | 'principalAmount' | 'amount'
> & {
  principalAmount: {
    amount: string;
    currency: string;
  };
  amount: {
    amount: string;
    currency: string;
  };
};

interface Params {
  id?: string;
}

export const PageLoansPaymentCreate: React.FC = () => {
  const currentPermission = useSessionStore((state) => state.currentPermission);
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const [bankAccountList, setBankAccountList] = useState<DropdownOption[]>([]);

  const formMethods = useForm<CreateLoanPaymentRequestForm>({
    defaultValues: {
      principalAmount: { amount: '', currency: '' },
      amount: { amount: '', currency: '' },
      loanId: id,
    },
  });

  const { handleSubmit } = formMethods;

  const handleCreate = (data: CreateLoanPaymentRequestForm) => {
    const currency = data.principalAmount?.currency || data.amount?.currency || 'USD';

    LoanRepository.createPayment({
      description: data.description,
      loanId: data.loanId,
      bankAccountId: data.bankAccountId,
      principalAmount: data.principalAmount?.amount,
      amount: data.amount?.amount,
      currency,
    })
      .then((response) => {
        navigate(`${ROUTE.LOANS}/edit/${data.loanId}`);
        addSuccessNotification({
          content: `${response.description} added`,
        });
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
        });
      });
  };

  const fetchBankAccounts = async (search?: string) => {
    let result: any = [];

    try {
      const response = await BankAccountRepository.getAll({ description: search });
      result = response.bankAccounts;
    } catch (error) {
      addDangerNotification({
        content: error as string,
      });
    }

    const entries: DropdownOption[] = [];
    result.map((entry: any) => {
      entries.push({
        label: `${
          entry?.displayName && entry?.description
            ? `${entry.displayName} – ${entry.description}`
            : entry?.description || 'Unnamed'
        } (${formatNumber(entry?.balances?.availableAmount)})`,
        small: entry.id,
        value: entry.id,
      });
    });

    setBankAccountList(entries);
  };

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  return (
    <>
      <PageActions>
        <Button
          onClick={() => navigate(id ? `${ROUTE.LOANS}/edit/${id}` : `${ROUTE.LOANS}/payments`)}
          variant="secondary"
          size="small"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(handleCreate)}
          isDisabled={currentPermission?.loans !== 'write'}
          size="small"
          icon={<Icon.Plus />}
        >
          Create Payment
        </Button>
      </PageActions>

      {currentPermission?.loans !== 'write' && (
        <Inner pb={0}>
          <RestrictedBanner />
        </Inner>
      )}
      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(handleCreate)}>
          {RenderFields<CreateLoanPaymentRequestForm>({
            sections: [
              {
                headline: 'Information',
                fields: [
                  {
                    id: 'description',
                    label: 'Description',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                      return (
                        <Input
                          value={value !== undefined ? String(value) : ''}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          placeholder="Description"
                          hasError={isTouched && !!error}
                        />
                      );
                    },
                  },
                  {
                    id: 'loanId',
                    label: 'Loan',
                    rules: { required: true },
                    children: ({ value, onChange }) => {
                      const loanId = typeof value === 'string' ? value : undefined;
                      return (
                        <LoanDropdown fullWidth maxWidth="640px" value={loanId} onChange={onChange} isDisabled={!!id} />
                      );
                    },
                  },
                  {
                    id: 'bankAccountId',
                    label: 'Bank Account',
                    rules: { required: true },
                    children: ({ value, onChange }) => {
                      return (
                        <Dropdown
                          options={bankAccountList}
                          active={value}
                          fullWidth
                          maxWidth="640px"
                          search
                          searchLabel="Search for description"
                          onSearchChange={(search) => fetchBankAccounts(search)}
                          onChange={onChange}
                        />
                      );
                    },
                  },
                  {
                    id: 'principalAmount',
                    label: 'Principal Amount',
                    rules: {
                      required: true,
                      validate: (value) =>
                        (value as CreateLoanPaymentRequestForm['principalAmount']).amount !== undefined,
                    },
                    children: ({ value, onChange }) => {
                      const { amount, currency } = value as CreateLoanPaymentRequestForm['principalAmount'];
                      return (
                        <AmountInput
                          value={amount ? parseInt(String(amount), 10) : undefined}
                          onChange={(newAmount) => onChange({ amount: newAmount, currency })}
                          onCurrencyCodeChange={(newCurrency) => onChange({ currency: newCurrency, amount: amount })}
                          currencyCode={currency}
                          currencyList={CurrenciesUSD}
                        />
                      );
                    },
                  },
                  {
                    id: 'amount',
                    label: 'Amount',
                    rules: {
                      required: true,
                      validate: (value) => (value as CreateLoanPaymentRequestForm['amount']).amount !== undefined,
                    },
                    children: ({ value, onChange }) => {
                      const { amount, currency } = value as CreateLoanPaymentRequestForm['amount'];
                      return (
                        <AmountInput
                          value={amount ? parseInt(String(amount), 10) : undefined}
                          onChange={(newAmount) => onChange({ amount: newAmount, currency })}
                          onCurrencyCodeChange={(newCurrency) => onChange({ currency: newCurrency, amount: amount })}
                          currencyCode={currency}
                          currencyList={CurrenciesUSD}
                        />
                      );
                    },
                  },
                ],
              },
            ],
          })}
        </form>
      </FormProvider>

      <Inner>
        <EditToolbar>
          <Button
            onClick={() => navigate(id ? `${ROUTE.LOANS}/edit/${id}` : `${ROUTE.LOANS}/payments`)}
            variant="secondary"
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(handleCreate)}
            isDisabled={currentPermission?.loans !== 'write'}
            size="small"
            icon={<Icon.Plus />}
          >
            Create Payment
          </Button>
        </EditToolbar>
      </Inner>
    </>
  );
};
